import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, Optional, Inject, provideAppInitializer, NgModule, Pipe, Directive } from '@angular/core';
import { Identifier, fusion, promisify, sleep } from '@js-dot/core';
import { DOCUMENT } from '@angular/common';
import { DoubleBraceTagParser, getTokenizedTemplateString } from '@js-dot/frame/template';
import * as i1 from '@ng-dot/core';
import { DotCoreModule, WithAsyncBase } from '@ng-dot/core';
import { BehaviorSubject, filter, map } from 'rxjs';
import { ROUTES } from '@angular/router';
import { semiMarkdown } from '@js-dot/frame';
const DOT_TRANSLATE_MODULE_CONFIGURE = new InjectionToken('#ng-dot/text/configure');
const DOT_TRANSLATE_FEATURE_CONFIGURE = new InjectionToken('#ng-dot/text/feature-configure');
const DOT_TEXT = new InjectionToken('#ng-dot/text/text');
const DOT_TEXT_WORD_LOADER = new InjectionToken('#ng-dot/text/word-loader');
const DOT_TRANSLATE = new Identifier('.translate');
const DOT_TRANSLATE_SERVICE = DOT_TRANSLATE.child('DotTranslateService');
class DotTranslateService {
  static language;
  static subLanguage;
  _rawWords;
  _words = {};
  _parser = new DoubleBraceTagParser();
  configure;
  language;
  subLanguage;
  wordIds;
  constructor(moduleConfigure, featureConfigures = []) {
    moduleConfigure = {
      disableSearchString: false,
      searchStringName: 'lang',
      wordLoader: defaultDefineLoader,
      phraseLoader: defaultPhraseLoader,
      isWordId: () => true,
      ...moduleConfigure
    };
    const document = inject(DOCUMENT);
    const {
      documentElement
    } = document;
    this.configure = moduleConfigure;
    this.language =
    // from URL
    (moduleConfigure.disableSearchString ? null : new URL(document.location.href).searchParams.get(moduleConfigure.searchStringName)
    // from configure
    ) || moduleConfigure.language
    // from attribute
    || documentElement.getAttribute('lang');
    this.subLanguage = moduleConfigure.subLanguage || 'en';
    DotTranslateService.language = this.language;
    DotTranslateService.subLanguage = this.subLanguage;
    documentElement.setAttribute('lang', this.language);
    const wordIds = moduleConfigure.wordIds || [];
    let builtinWords = {};
    let current = featureConfigures?.length || 0;
    while (current-- > 0) {
      const {
        wordId,
        words
      } = featureConfigures[current];
      if (wordId) {
        wordIds[wordIds.length] = wordId;
      }
      if (words) {
        builtinWords = fusion(builtinWords, words);
      }
    }
    this.wordIds = wordIds;
    this._rawWords = fusion(builtinWords, moduleConfigure.words);
  }
  _setWords(wordSet) {
    const {
      language,
      _rawWords
    } = this;
    let words = {};
    let current = wordSet.length;
    while (current-- > 0) {
      words = {
        ...words,
        ...wordSet[current]
      };
    }
    _rawWords[language] = {
      ...words,
      ...(_rawWords[language] || {})
    };
  }
  /**
   * Check the defines includes id
   * @param id
   */
  isDefineId(id) {
    return this.configure.isWordId(id);
  }
  /**
   * @deprecated using `word()` method
   * @param id
   */
  define(id) {
    return this.word(id);
  }
  /**
   * @deprecated using `words()` method
   * @param ids
   */
  defines(...ids) {
    return this.words(...ids);
  }
  /**
   * Get a word
   *
   * @example
   * ```typescript
   * @Injectable()
   * export class FooService {
   *     constructor(_translate: DotTranslateService){
   *			const FOO = _translate.word('FOO');
   *     }
   * }
   * ```
   * @param id
   */
  word(id) {
    const {
      _words
    } = this;
    let word = _words[id];
    if (!word) {
      if (this.configure.isWordId(id)) {
        const {
          language,
          subLanguage,
          _rawWords,
          _parser
        } = this;
        word = (_rawWords[language] || {})[id] || (_rawWords[subLanguage] || {})[id] || 'Unknown::' + id;
        const token = _parser.getToken(word);
        if (token[1].length) {
          word = getTokenizedTemplateString(id => {
            return this.word(id);
          }, token, null);
        }
        _words[id] = word;
      } else {
        _words[id] = id;
        word = id;
      }
    }
    return word;
  }
  /**
   * Get multiple words as Array
   *
   * @example
   * ```typescript
   * @Injectable()
   * export class FooService {
   *     constructor(_translate: DotTranslateService){
   *			const [FOO, BAR] = _translate.define('FOO', 'BAR');
   *     }
   * }
   * ```
   * @param ids
   */
  words(...ids) {
    const defines = [];
    const end = ids.length;
    let current = -1;
    while (++current < end) {
      defines[defines.length] = this.word(ids[current]);
    }
    return defines;
  }
  static ɵfac = function DotTranslateService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTranslateService)(i0.ɵɵinject(DOT_TRANSLATE_MODULE_CONFIGURE, 8), i0.ɵɵinject(DOT_TRANSLATE_FEATURE_CONFIGURE, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotTranslateService,
    factory: DotTranslateService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTranslateService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DOT_TRANSLATE_MODULE_CONFIGURE]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DOT_TRANSLATE_FEATURE_CONFIGURE]
    }]
  }], null);
})();
/**
 * Translation Module
 */
class DotTranslateModule {
  /**
   * Set Application Module's Translate Configure
   * @example
   * ```angular
   * @NgModule({
   *  imports: [
   * 	DotTranslateModule.forRoot({
   * 	    // Built-in Defines
   * 	    // Override Other Values
   * 	    words: {
   * 	        en: {
   * 	            FOO: 'foo',
   * 	            BAR: 'bar'
   * 	        }
   * 	    }
   * 	})
   * 	]
   * })
   * export class AppModule {
   *
   * }
   * ```
   * @param configure
   */
  static forRoot(configure) {
    return {
      ngModule: DotTranslateModule,
      providers: [{
        provide: DOT_TRANSLATE_MODULE_CONFIGURE,
        useValue: configure
      }, provideAppInitializer(() => textInitializeFunction())]
    };
  }
  /**
   * Set Library Module's Text Configures
   * @example
   * ```angular
   * @NgModule({
   *  imports: [
   * 	DotTranslateModule.forFeature({
   * 		// External Defines ID for JSON
   * 	    wordId: 'app',
   *
   * 	    // Built-in Defines
   * 	    words: {
   * 	        en: {
   * 	            FOO: 'foo',
   * 	            BAR: 'bar'
   * 	        }
   * 	    }
   * 	})
   * 	]
   * })
   * export class FooModule {
   *
   * }
   * ```
   * @param configure
   */
  static forFeature(configure) {
    return {
      ngModule: DotTranslateModule,
      providers: [{
        provide: DOT_TRANSLATE_FEATURE_CONFIGURE,
        useValue: configure,
        multi: true
      }]
    };
  }
  static ɵfac = function DotTranslateModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTranslateModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DotTranslateModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    imports: [DotCoreModule.forFeature({
      namespace: DOT_TRANSLATE,
      providers: {
        [DOT_TRANSLATE_SERVICE.symbol]: DotTranslateService
      }
    })]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTranslateModule, [{
    type: NgModule,
    args: [{
      imports: [DotCoreModule.forFeature({
        namespace: DOT_TRANSLATE,
        providers: {
          [DOT_TRANSLATE_SERVICE.symbol]: DotTranslateService
        }
      })]
    }]
  }], null, null);
})();
function defaultDefineLoader(language, id) {
  return fetch(`/assets/texts/defines/${language}/${id}.json`).then(toJson).catch(returnEmpty);
}
function defaultPhraseLoader(language, id) {
  return fetch(`/assets/texts/${language}/${id}.json`).then(toJson).catch(returnEmpty);
}
function toJson(response) {
  return response.json();
}
function returnEmpty() {
  return {};
}
function textInitializeFunction() {
  const _translate = inject(DotTranslateService);
  const {
    configure: {
      initialize
    }
  } = _translate;
  return (initialize ? initialize() : Promise.resolve()).then(() => {
    const {
      wordIds,
      configure: {
        wordLoader
      },
      language
    } = _translate;
    const promises = [];
    let current = wordIds.length;
    while (current-- > 0) {
      const defineId = wordIds[current];
      promises[promises.length] = wordLoader(language, defineId);
    }
    return Promise.all(promises).then(stores => {
      _translate._setWords(stores);
    });
  });
}
class DotOfDefinePipe {
  _translate;
  constructor(_translate) {
    this._translate = _translate;
  }
  /**
   * String transform to Translate Defined Value
   */
  transform(id) {
    return this._translate.word(id);
  }
  static ɵfac = function DotOfDefinePipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotOfDefinePipe)(i0.ɵɵdirectiveInject(DotTranslateService, 16));
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "ofDefine",
    type: DotOfDefinePipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotOfDefinePipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'ofDefine'
    }]
  }], () => [{
    type: DotTranslateService
  }], null);
})();
class DotTextService {
  _translate;
  id;
  _load = new BehaviorSubject(false);
  _store = {};
  _subjects = {};
  constructor(_translate, id) {
    this._translate = _translate;
    this.id = id;
    promisify(_translate.configure.phraseLoader(_translate.language, id)).then(values => {
      this.update(id, values);
      this._load.next(true);
    });
  }
  update(id, values) {
    const {
      _store
    } = this;
    if (_store[id]) {
      _store[id] = Object.assign(_store[id], values);
    } else {
      _store[id] = values;
    }
  }
  get(path) {
    if (typeof path === 'string') {
      return this._load.pipe(filter(state => state), map(() => {
        const {
          _store
        } = this;
        const store = _store[this.id];
        return store && store[path] || 'Unknown::' + path;
      }));
    }
    return path;
  }
  static ɵfac = function DotTextService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTextService)(i0.ɵɵinject(DotTranslateService), i0.ɵɵinject(DOT_TEXT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotTextService,
    factory: DotTextService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTextService, [{
    type: Injectable
  }], () => [{
    type: DotTranslateService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [DOT_TEXT]
    }]
  }], null);
})();
class OfTextPipe extends WithAsyncBase {
  _text;
  constructor(_text) {
    super();
    this._text = _text;
  }
  transform(id) {
    return this.transformWithAsync(arguments);
  }
  getValue(id) {
    return this._text.get(id);
  }
  static ɵfac = function OfTextPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || OfTextPipe)(i0.ɵɵdirectiveInject(DotTextService, 16));
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "ofText",
    type: OfTextPipe,
    pure: false,
    standalone: false
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(OfTextPipe, [{
    type: Pipe,
    args: [{
      name: 'ofText',
      pure: false,
      standalone: false
    }]
  }], () => [{
    type: DotTextService
  }], null);
})();
class DotTextModule {
  static forFeature(id) {
    return {
      ngModule: DotTextModule,
      providers: [{
        provide: DOT_TEXT,
        useValue: id
      }]
    };
  }
  static ɵfac = function DotTextModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTextModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DotTextModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [DotTextService]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTextModule, [{
    type: NgModule,
    args: [{
      declarations: [OfTextPipe],
      exports: [OfTextPipe],
      providers: [DotTextService]
    }]
  }], null, null);
})();
class DotTextAwaitService {
  constructor() {
    console.log(this.constructor);
  }
  static ɵfac = function DotTextAwaitService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTextAwaitService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotTextAwaitService,
    factory: DotTextAwaitService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTextAwaitService, [{
    type: Injectable
  }], () => [], null);
})();
class DotTextAwaitModule {
  static forFeature(id) {
    console.warn(id);
    return {
      ngModule: DotTextAwaitModule,
      providers: [{
        provide: ROUTES,
        useFactory: _service => {
          return sleep(1000).then(() => []);
        },
        deps: [DotTextAwaitService],
        multi: true
      }]
    };
  }
  static ɵfac = function DotTextAwaitModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTextAwaitModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DotTextAwaitModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [DotTextAwaitService]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTextAwaitModule, [{
    type: NgModule,
    args: [{
      providers: [DotTextAwaitService]
    }]
  }], null, null);
})();
const DOUBLE_BRACE_PARSER = new DoubleBraceTagParser();
class DotPhraseOperatorBase {
  _translate;
  _cache = {};
  constructor(_translate) {
    this._translate = _translate;
  }
  _getTemplate(token) {
    const _phrases = this._getPhrases();
    const {
      _translate
    } = this;
    const end = _phrases.length;
    let current = -1;
    while (++current < end) {
      const texts = _phrases[current];
      if (!(token in texts)) {
        continue;
      }
      let text = texts[token];
      const [_, variables, sytaxes] = DOUBLE_BRACE_PARSER.getToken(text);
      const remains = [];
      if (!variables?.length) {
        return {
          template: text
        };
      }
      let cursor = variables.length;
      while (cursor-- > 0) {
        const variable = variables[cursor];
        const syntax = sytaxes[cursor];
        if (_translate.isDefineId(variable)) {
          text = text.replace(syntax, _translate.define(variable));
          continue;
        }
        remains[remains.length] = [variable, syntax];
      }
      return {
        template: text,
        variables: remains
      };
    }
    return {
      template: 'Text::' + token
    };
  }
  _getCache(token) {
    const {
      _cache
    } = this;
    return _cache[token] || (_cache[token] = this._getTemplate(token));
  }
  has(needle) {
    const _phrases = this._getPhrases();
    let current = _phrases.length;
    while (current-- > 0) {
      for (const token in _phrases[current]) {
        if (needle === token) {
          return true;
        }
      }
    }
    return false;
  }
  get(token, values) {
    let {
      template,
      variables
    } = this._getCache(token);
    let current;
    if ((values || values === 0) && variables) {
      current = variables.length;
      while (current-- > 0) {
        const [name, syntax] = variables[current];
        const value = name === 'this' ? values : values[name];
        template = template.replace(syntax, value);
      }
    }
    const {
      _translate: {
        configure: {
          phraseMethods
        }
      }
    } = this;
    if (!phraseMethods) {
      return template;
    }
    const methodExpressions = template.match(/([^\s]+)\([^)]+\)/g);
    if (!methodExpressions) {
      return template;
    }
    current = methodExpressions.length;
    while (current-- > 0) {
      const methodExpression = methodExpressions[current];
      const firstBraceIndex = methodExpression.indexOf('(');
      const word = methodExpression.substring(0, firstBraceIndex);
      const argString = methodExpression.slice(firstBraceIndex);
      const args = argString.substring(1, argString.length - 1).split(/\s*,\s*/);
      let cursor = phraseMethods.length;
      while (cursor-- > 0) {
        const method = phraseMethods[cursor];
        if (method.pattern.test(args[0])) {
          template = template.replace(methodExpression, method.transform(word, args, values));
          break;
        }
      }
    }
    return semiMarkdown(template);
  }
  gets(tokens) {
    const result = {};
    if (Array.isArray(tokens)) {
      let current = tokens.length;
      while (current-- > 0) {
        const token = tokens[current];
        result[token] = this.get(token);
      }
      return result;
    } else {
      for (const rename in tokens) {
        if (!tokens.hasOwnProperty(rename)) {
          continue;
        }
        result[rename] = this.get(tokens[rename]);
      }
    }
    return result;
  }
  getAll() {
    const _phrases = this._getPhrases();
    const result = {};
    let current = _phrases.length;
    while (current-- > 0) {
      const text = _phrases[current];
      for (const token in text) {
        if (text.hasOwnProperty(token) && !token.startsWith('$')) {
          result[token] = this.get(token);
        }
      }
    }
    return result;
  }
  static ɵfac = function DotPhraseOperatorBase_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotPhraseOperatorBase)(i0.ɵɵdirectiveInject(DotTranslateService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotPhraseOperatorBase
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotPhraseOperatorBase, [{
    type: Directive
  }], () => [{
    type: DotTranslateService
  }], null);
})();

/**
 * Built-in Phrases Base
 */
class DotPhraseBase extends DotPhraseOperatorBase {
  _phrases;
  constructor(_translate) {
    super(_translate);
  }
  _getPhrases() {
    return this._phrases || (this._phrases = [this.phrases[this._translate.language] || this.phrases[this._translate.subLanguage] || {}]);
  }
  static ɵfac = function DotPhraseBase_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotPhraseBase)(i0.ɵɵdirectiveInject(DotTranslateService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotPhraseBase,
    standalone: false,
    features: [i0.ɵɵInheritDefinitionFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotPhraseBase, [{
    type: Directive,
    args: [{
      standalone: false
    }]
  }], () => [{
    type: DotTranslateService
  }], null);
})();
class DotLazyPhraseService extends DotPhraseOperatorBase {
  _load;
  _load$ = new BehaviorSubject(false);
  _phrases;
  _getPhrases() {
    return this._phrases;
  }
  /**
   * External Resource Loading State
   */
  load$ = this._load$.pipe(filter(flag => flag));
  load(...ids) {
    if (this._load) {
      return new this.constructor(this._translate).load(...ids);
    }
    this._load = true;
    const {
      _translate: {
        configure
      }
    } = this;
    const promises = [];
    const end = ids.length;
    let current = -1;
    while (++current < end) {
      promises[current] = configure.phraseLoader(this._translate.language, ids[current]);
    }
    Promise.all(promises).then(texts => {
      this._phrases = texts;
      this._load$.next(true);
    });
    return this;
  }
  get$(token, values) {
    return new Promise(resolve => {
      this.load$.subscribe(() => {
        resolve(this.get(token, values));
      });
    });
  }
  gets$(tokens) {
    return new Promise(resolve => {
      this.load$.subscribe(() => {
        resolve(this.gets(tokens));
      });
    });
  }
  getAll$() {
    return new Promise(resolve => {
      this.load$.subscribe(() => {
        resolve(this.getAll());
      });
    });
  }
  static ɵfac = /* @__PURE__ */(() => {
    let ɵDotLazyPhraseService_BaseFactory;
    return function DotLazyPhraseService_Factory(__ngFactoryType__) {
      return (ɵDotLazyPhraseService_BaseFactory || (ɵDotLazyPhraseService_BaseFactory = i0.ɵɵgetInheritedFactory(DotLazyPhraseService)))(__ngFactoryType__ || DotLazyPhraseService);
    };
  })();
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotLazyPhraseService,
    factory: DotLazyPhraseService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotLazyPhraseService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/**
 * DotSelfLazyPhraseService.get() as Pipe syntax
 */
class Text$Pipe extends WithAsyncBase {
  transform(_phrase, token, values) {
    return this.transformWithAsync(arguments);
  }
  getValue(_phrase, token, values) {
    return _phrase.get$(token, values);
  }
  static ɵfac = /* @__PURE__ */(() => {
    let ɵText$Pipe_BaseFactory;
    return function Text$Pipe_Factory(__ngFactoryType__) {
      return (ɵText$Pipe_BaseFactory || (ɵText$Pipe_BaseFactory = i0.ɵɵgetInheritedFactory(Text$Pipe)))(__ngFactoryType__ || Text$Pipe);
    };
  })();
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "text$",
    type: Text$Pipe,
    pure: false
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Text$Pipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'text$',
      pure: false
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DOT_TEXT, DOT_TEXT_WORD_LOADER, DOT_TRANSLATE, DOT_TRANSLATE_FEATURE_CONFIGURE, DOT_TRANSLATE_MODULE_CONFIGURE, DOT_TRANSLATE_SERVICE, DotLazyPhraseService, DotOfDefinePipe, DotPhraseBase, DotTextAwaitModule, DotTextAwaitService, DotTextModule, DotTextService, DotTranslateModule, DotTranslateService, OfTextPipe, Text$Pipe, defaultDefineLoader, defaultPhraseLoader };
